import { notify } from "@/components/common/Notification";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { axiosConfig } from "./axiosConfig";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";

function shouldHideApiMessage(url?: string): boolean {
  return hidenMessageAPI.some(path => (url ?? "").includes(path));
}

export interface ApiError {
  message: string;
  status: number;
  errors?: any;
}

export interface RequestConfig extends AxiosRequestConfig {
  showNotification?: boolean;
  customErrorMessage?: string;
}

function shouldSuppressNotification(config?: RequestConfig): boolean {
  return (
    config?.showNotification === false || shouldHideApiMessage(config?.url)
  );
}

function handleRequestError(error: unknown, config?: RequestConfig): never {
  const axiosError = error as {
    config?: RequestConfig;
    code?: string;
    message?: string;
  };
  if (!shouldSuppressNotification(config ?? axiosError?.config)) {
    handelError(axiosError);
  }
  throw error;
}

export const get = async <T = any>(
  url: string,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.get(url, config);
    return response.data;
  } catch (error: unknown) {
    handleRequestError(error, config);
  }
};

export const post = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.post(
      url,
      data,
      config
    );
    return response.data;
  } catch (error: unknown) {
    handleRequestError(error, config);
  }
};

export const put = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.put(url, data, config);
    return response.data;
  } catch (error: unknown) {
    handleRequestError(error, config);
  }
};

export const patch = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.patch(
      url,
      data,
      config
    );
    return response.data;
  } catch (error: unknown) {
    handleRequestError(error, config);
  }
};

export const del = async <T = any>(
  url: string,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.delete(url, config);
    return response.data;
  } catch (error: unknown) {
    handleRequestError(error, config);
  }
};

const handelError = (error: { code?: string; message?: string }) => {
  if (
    error.code === "ERR_NETWORK" ||
    error.message?.includes("Network Error")
  ) {
    notify.error(
      "Network Error",
      "Unable to connect to the server. Please check your internet connection."
    );
  }
};
